/**
 * Performance Utilities
 * 
 * Lightweight helpers for debouncing and throttling to reduce
 * performance violations in event handlers and timers.
 */

/**
 * Debounce function - delays execution until after wait time has passed
 * since the last invocation
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function - limits execution to at most once per wait time
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false;
  
  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, wait);
    }
  };
}

/**
 * Lightweight logger that only logs in development
 * Reduces console noise in production
 */
export const perfLog = {
  log: (...args: any[]) => {
    // Logging disabled
  },
  warn: (...args: any[]) => {
    // Logging disabled
  },
  error: (...args: any[]) => {
    // Logging disabled
  },
  debug: (...args: any[]) => {
    // Logging disabled
  },
};

